import { Avatar } from "src/ccl/blocks/avatar";
import { Text } from "src/ccl/document";
import { Dropdown, ExternalLink, Link } from "src/ccl/navigation";
import { styled } from "src/ccl/stitches";
import { UserKindContext, useFeatures, useStoreModel } from "src/hooks";
import {
  canSetUpAgency,
  rememberWorkspaceIntent,
} from "src/utils/workspaceSetupIntent";
import { useNewDashboard } from "src/hooks/useNewDashboard";

const ProfileDropdownBody = styled("div", {
  p: "$4",
});

const ProfileDropdownLauncher = styled("button", {
  background: "transparent",
  border: 0,
  cursor: "pointer",
  display: "none",
  minHeight: "$12",
  minWidth: "$12",
  p: 0,

  "@bp2": {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
});

const profileDropdownLinkStyles = {
  display: "block",
  minWidth: "300px",
  textDecoration: "none",
  lineHeight: "$26",
  my: "$4",

  "&:last-child": {
    mb: "$0",
  },
};

const ProfileDropdownLink = styled(Link, profileDropdownLinkStyles);
const ProfileDropdownSignOut = styled(ExternalLink, profileDropdownLinkStyles);

interface ProfileDropdownProps {
  avatarImageUrl?: string;
  avatarInitials?: string;
  onSignOut?: () => void;
  userName?: string;
  userEmail?: string;
  context: UserKindContext;
}

export const ProfileDropdown = ({
  avatarImageUrl,
  avatarInitials,
  onSignOut,
  userName,
  userEmail,
  context,
}: ProfileDropdownProps) => {
  const { featureEnabled } = useFeatures();
  const { me } = useStoreModel("currentUser");
  const { isNewDashboard } = useNewDashboard();

  // Only clients and agents have account pages on the new trees; a talent
  // keeps /account/* whatever the opt-out says, and a third-party agent is an
  // agent. Every header mounts this with the context, so derive the role
  // here rather than from per-header props. Opted-out users keep the legacy
  // links until U18 removes the opt-out.
  const role = context === "TPA" ? "agent" : context;
  const onNewTree = (role === "agent" || role === "client") && isNewDashboard;

  const navigateTo = (option: string) => {
    const linkMapping: Record<string, string> = {
      dashboard: `/dashboard/${role}/jobs`,
      settings: `/dashboard/${role}/account/settings`,
      password: `/dashboard/${role}/account/password`,
    };

    return linkMapping[option];
  };
  return (
    <Dropdown
      position="right"
      launcher={
        <ProfileDropdownLauncher
          aria-label="Open profile menu"
          data-test-id="HeaderAvatar"
          type="button"
          css={
            featureEnabled("unified_dashboards")
              ? {
                  display: "none",
                  "@md": {
                    display: "flex",
                  },
                }
              : {}
          }
        >
          <Avatar
            css={{
              backgroundColor: "$grey2",
              border: avatarImageUrl ? "none" : "2px solid $black",
            }}
            fontColor="black"
            initials={avatarInitials}
            imageUrl={avatarImageUrl}
          />
        </ProfileDropdownLauncher>
      }
      wrapperCss={
        featureEnabled("unified_dashboards")
          ? {
              display: "none",
              "@md": {
                display: "initial",
              },
            }
          : {}
      }
    >
      <ProfileDropdownBody data-test-id="ProfileDropdownBody">
        <Text
          variant="meta"
          css={{
            fontWeight: "$bold",
            minWidth: "300px",
            color: "$grey6",
            mb: "$3",
          }}
        >
          {userName}
        </Text>

        <Text
          variant="meta"
          css={{
            minWidth: "300px",
            color: "$grey6",
            mb: "$3",
            borderBottom: "1px $grey2 solid",
            paddingBottom: "$4",
          }}
        >
          {userEmail}
        </Text>

        {context === "client" && (
          <ProfileDropdownLink
            to={onNewTree ? navigateTo("dashboard") : "/dashboard/client"}
          >
            Dashboard
          </ProfileDropdownLink>
        )}

        <ProfileDropdownLink
          to={onNewTree ? navigateTo("settings") : "/account/settings"}
        >
          Settings
        </ProfileDropdownLink>

        {canSetUpAgency(me) && (
          <ProfileDropdownLink
            to="/account/add-workspace?workspace=agent"
            onClick={() => rememberWorkspaceIntent("agent", me?.id)}
          >
            Set up an agency
          </ProfileDropdownLink>
        )}

        {context === "talent" && featureEnabled("unified_dashboards") && (
          <ProfileDropdownLink to="/account/payments">
            Payments
          </ProfileDropdownLink>
        )}

        <ProfileDropdownLink
          to={onNewTree ? navigateTo("password") : "/account/password"}
        >
          Change password
        </ProfileDropdownLink>

        <ProfileDropdownSignOut
          to="/"
          eventName="header:signOut"
          onClick={(e) => {
            e.preventDefault();

            if (onSignOut) {
              onSignOut();
            }
          }}
        >
          Sign out
        </ProfileDropdownSignOut>
      </ProfileDropdownBody>
    </Dropdown>
  );
};
